import React, { useState, useMemo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/default/button';

import type {
	CommentLocation,
	CommentReply,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import {
	CommentType,
	CommentActionType,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	useCommentsContentActions,
	useCommentsContentState,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { RepliesToggler } from '@confluence/comments-common/entry-points/RepliesToggler.ts';
import { RepliesFooter } from '@confluence/comments-common/entry-points/RepliesFooter.ts';
import { useUpdateVisibleReplies } from '@confluence/comments-common/entry-points/useUpdateVisibleReplies.ts';
import { useVisibleReplies } from '@confluence/comments-common/entry-points/useVisibleReplies.ts';
import {
	ViewReplyOptions,
	DEFAULT_NUM_VISIBLE_REPLIES,
} from '@confluence/comments-common/entry-points/repliesHelpers.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { ObjectCommentNestableReplies } from './ObjectCommentNestableReplies.tsx';
import { ObjectCommentReplyEditor } from './ObjectCommentReplyEditor.tsx';

const replyActionButtonStyles = {
	marginLeft: token('space.150'),
	width: '62px',
	height: '24px',
	left: '-12px',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
};

export type ObjectCommentRepliesProps = {
	parentComment: CommentData;
	pageInfo: PageInfoNode | null;
	hoveredCommentId: string;
	pageMode: PageMode;
	parentCommentId: string;
	handleMouseEnter: (id?: string) => void;
	handleMouseLeave: () => void;
	canAddComments: boolean;
	isOffline?: boolean;
	flags?: FlagsStateContainer;
	isEditorExpanded: boolean;
};

const i18n = defineMessages({
	replyAction: {
		id: 'object-comments.reply.action',
		defaultMessage: 'Reply',
		description: 'Reply button label for comment thread in comments panel',
	},
});

export const ObjectCommentReplies = ({
	parentComment,
	pageInfo,
	hoveredCommentId,
	pageMode,
	parentCommentId,
	handleMouseEnter,
	handleMouseLeave,
	canAddComments,
	isOffline = false,
	flags,
	isEditorExpanded,
}: ObjectCommentRepliesProps): React.JSX.Element => {
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();

	const { setReplyToCommentId } = useCommentsPanelActions();

	const allReplies = useMemo(() => {
		return parentComment.replies.filter((r) => (r as CommentReply) !== null);
	}, [parentComment.replies]);

	const numOfReplies = allReplies.length;

	const { unreadCommentIDSet } = useUnreadCommentsState();

	const unreadReplyIds = useMemo(() => {
		return allReplies.filter((r) => unreadCommentIDSet.has(r.id)).map((r) => r.id);
	}, [allReplies, unreadCommentIDSet]);

	const [legacyVisibleReplies, setVisibleReplies] = useState<ReplyData[]>([]);

	const { showModal } = useDialogs();

	const commentType = parentComment.type;
	const isParentCommentOpen = parentComment.isOpen;

	const [currentReplyView, setReplyView] = useState(
		// On the panel view we use a function for this
		// On the object comments we default to all no matter what
		ViewReplyOptions.ALL,
	);

	// User page permissions

	// The user can upload media only if they have update permissions for the page

	const threadKey =
		commentType === CommentType.INLINE
			? (parentComment?.location as CommentLocation).inlineMarkerRef || ''
			: parentCommentId;

	const supportedTopLevelActions: CommentAction[] = isParentCommentOpen
		? ['edit', 'resolve', 'delete']
		: ['reopen']; // reopen won't be rendered for reply, but we are using it to render disabled reaction summary for replies

	const expandReplyEditor = isEditorExpanded;

	const topLevelReplyToNestedChildrenMap = useMemo(() => {
		const topLevelReplyIds = new Set(
			allReplies.filter((reply) => reply.parentId === parentCommentId).map((reply) => reply.id),
		);

		// We need to get all replies that are nested to any depth under a top level reply.
		// This will help us render the GeneralReplyScroller component for each reply (mainly, we need to show user icons and the blue dot indicator)
		const topLevelReplyToNestedChildrenMapInternal: { [key: string]: ReplyData[] } = Array.from(
			topLevelReplyIds,
		).reduce<{ [key: string]: ReplyData[] }>((acc, replyId) => {
			acc[replyId] = [];
			return acc;
		}, {});

		const replyIdToReplyMap = allReplies.reduce<{ [key: string]: ReplyData }>((acc, reply) => {
			acc[reply.id] = reply;
			return acc;
		}, {});

		// Map all replies to their top level parent. Replies can be nested to any depth, so we need to traverse the tree.
		const filteredReplies = allReplies.filter((reply) => !topLevelReplyIds.has(reply.id));

		for (const filteredReply of filteredReplies) {
			let replyParentId = filteredReply.parentId;

			// Add a circuit breaker to prevent infinite loops if we get really wonky data
			let circuitBreaker = 0;
			while (replyParentId && circuitBreaker < 100 && !topLevelReplyIds.has(replyParentId)) {
				replyParentId = replyIdToReplyMap[replyParentId]?.parentId;
				circuitBreaker++;
			}

			if (replyParentId && replyParentId in topLevelReplyToNestedChildrenMapInternal) {
				// Second condition will always be true, unless we somehow hit circuit breaker above
				topLevelReplyToNestedChildrenMapInternal[replyParentId].push(filteredReply);
			}
		}

		return topLevelReplyToNestedChildrenMapInternal;
	}, [allReplies, parentCommentId]);

	// Determine the latest top level replies to show on the thread's initial load
	const latestReplies = useMemo(() => {
		const replies: ReplyData[] = [];
		const topLevelReplies = allReplies.filter(
			(reply) => reply.id in topLevelReplyToNestedChildrenMap,
		);

		// Sort by createdAtNonLocalized value
		topLevelReplies.sort((a, b) => {
			// Safely handle missing or invalid dates
			const dateA = getValidDate(a?.createdAtNonLocalized);
			const dateB = getValidDate(b?.createdAtNonLocalized);

			return dateA.getTime() - dateB.getTime();
		});

		// Add the latest replies to the replies array
		for (let i = 0; i < DEFAULT_NUM_VISIBLE_REPLIES; i++) {
			if (topLevelReplies.length > 0) {
				const reply = topLevelReplies.pop();
				if (reply) {
					// Unshift to maintain reply chronological order
					replies.unshift(reply);
				}
			}
		}

		return replies;
	}, [allReplies, topLevelReplyToNestedChildrenMap]);

	/*
	 * Determine the latest top level reply IDs and their nested reply IDs.
	 * This is passed to RepliesToggler to determine the hidden replies.
	 */
	const latestReplyIds = useMemo(() => {
		const latestAndNestedReplyIds: Set<string> = new Set();

		for (const reply of latestReplies) {
			latestAndNestedReplyIds.add(reply.id);

			for (const nestedReply of topLevelReplyToNestedChildrenMap[reply.id]) {
				latestAndNestedReplyIds.add(nestedReply.id);
			}
		}

		return latestAndNestedReplyIds;
	}, [latestReplies, topLevelReplyToNestedChildrenMap]);

	const parentToRepliesMap = useMemo(() => {
		const parentToRepliesMapInternal: { [key: string]: ReplyData[] } = {
			[parentCommentId]: [],
			...allReplies.reduce(
				(acc, reply) => ({
					...acc,
					[reply.id]: [],
				}),
				{},
			),
		};

		for (const reply of allReplies) {
			if (reply.parentId && reply.parentId in parentToRepliesMapInternal) {
				// Second condition will always be true, unless we get really wonky data
				parentToRepliesMapInternal[reply.parentId].push(reply);
			}
		}

		return parentToRepliesMapInternal;
	}, [allReplies, parentCommentId]);

	useUpdateVisibleReplies({
		allReplies,
		isParentCommentOpen,
		currentReplyView,
		latestReplies,
		parentCommentId,
		setVisibleReplies,
		allowNestedReplies: true,
	});

	// Render-time derivation of visible replies. Computed unconditionally to
	// satisfy the rules of hooks; the experiment gate below selects which value
	// is consumed. Computing during render (rather than syncing state in a
	// layout effect) lets visible replies appear in SSR HTML and removes a
	// post-mount re-render on the client.
	const derivedVisibleReplies = useVisibleReplies({
		allReplies,
		isParentCommentOpen,
		currentReplyView,
		latestReplies,
		parentCommentId,
		allowNestedReplies: true,
	});

	const visibleReplies = expValEquals('confluence_fe_ssr_top_footer_comments', 'isEnabled', true)
		? derivedVisibleReplies
		: legacyVisibleReplies;

	const handleReplyButtonClick = () => {
		if (hasContentChanged) {
			// if the content has changed and the user has clicked reply button to another comment thread, warn the user
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					setReplyToCommentId({ parentCommentId, location: LocationValue.FOOTER });
				},
			});
		} else {
			setReplyToCommentId({ parentCommentId, location: LocationValue.FOOTER });
		}
	};

	const canShowReplyButton = isParentCommentOpen && canAddComments;

	const renderFooter = () =>
		expandReplyEditor ? (
			<ObjectCommentReplyEditor
				pageInfo={pageInfo}
				pageMode={pageMode}
				setReplyView={setReplyView}
				parentComment={parentComment}
				threadKey={threadKey}
				replyToId={parentCommentId}
				isOffline={isOffline}
			/>
		) : canShowReplyButton ? (
			<WithCurvedBranchingStyle
				showDefaultStyles
				customStyles={replyActionButtonStyles}
				testId="comments-panel-reply-comment-action"
				onClick={handleReplyButtonClick}
			>
				<Button
					data-cy="comments-panel-reply-comment-action"
					appearance="subtle"
					isDisabled={!isParentCommentOpen || isOffline}
					analyticsContext={{
						attributes: {
							name: 'replyToCommentButton',
							source: AnalyticsSource.COMMENTS_PANEL,
						},
					}}
					interactionName="comments-panel-reply-comment-action"
				>
					<FormattedMessage {...i18n.replyAction} />
				</Button>
			</WithCurvedBranchingStyle>
		) : null;

	const renderRepliesToggler = () => {
		return (
			<RepliesToggler
				replies={allReplies}
				unreadReplyIds={unreadReplyIds}
				setReplyView={setReplyView}
				currentReplyView={currentReplyView}
				commentType={commentType}
				parentCommentId={parentCommentId}
				isParentOpen={isParentCommentOpen}
				canAddComments={canAddComments}
				latestReplyIds={latestReplyIds}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				analyticsSource={AnalyticsSource.PAGE_FOOTER}
				expandReplyEditor={expandReplyEditor}
			/>
		);
	};

	const wasRemovedByAnotherUser = parentComment
		? parentComment.wasRemovedByAnotherUser === CommentActionType.DELETE_COMMENT ||
			parentComment.wasRemovedByAnotherUser === CommentActionType.RESOLVE_COMMENT_THREAD
		: false;

	const repliesBoxStyle = useMemo<React.CSSProperties>(() => {
		const baseStyle: React.CSSProperties = {
			display: 'flex',
			flexDirection: 'column',
			paddingLeft: token('space.200'),
			paddingRight: token('space.100'),
		};
		if (wasRemovedByAnotherUser) {
			baseStyle.opacity = '50%';
		}

		if (expValEquals('confluence_comments_tuneup', 'isEnabled', true)) {
			baseStyle.paddingLeft = token('space.150');
		}

		return baseStyle;
	}, [wasRemovedByAnotherUser]);

	return (
		<>
			{numOfReplies > 0 &&
				expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
				renderRepliesToggler()}
			<div
				data-testid={`${LocationValue.FOOTER}-comment-thread-${parentComment.id}-replies`}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Using style prop to avoid Babel compilation issues with css prop
				style={repliesBoxStyle}
			>
				{numOfReplies > 0 &&
					!expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
					renderRepliesToggler()}
				{visibleReplies.length > 0 && (
					<ObjectCommentNestableReplies
						parentReplyId={parentCommentId}
						parentToRepliesMap={parentToRepliesMap}
						handleMouseEnter={handleMouseEnter}
						handleMouseLeave={handleMouseLeave}
						canAddComments={canAddComments}
						pageInfo={pageInfo}
						pageMode={pageMode}
						threadKey={threadKey}
						supportedTopLevelActions={supportedTopLevelActions}
						hoveredCommentId={hoveredCommentId}
						isTopLevelReply
						parentComment={parentComment}
						parentReplyButtonAllowed={canShowReplyButton}
						setReplyView={setReplyView}
						isOffline={isOffline}
						flags={flags}
						isEditorExpanded={isEditorExpanded}
					/>
				)}
				{expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
					<RepliesFooter
						replyView={currentReplyView}
						setReplyView={setReplyView}
						isEditorExpanded={isEditorExpanded}
						isParentCommentOpen={isParentCommentOpen}
						handleReplyButtonClick={handleReplyButtonClick}
						numOfVisibleReplies={visibleReplies ? visibleReplies.length : 0}
						isOffline={isOffline}
						scrollToId={parentCommentId}
						location={LocationValue.FOOTER}
					>
						<ObjectCommentReplyEditor
							pageInfo={pageInfo}
							pageMode={pageMode}
							setReplyView={setReplyView}
							parentComment={parentComment}
							threadKey={threadKey}
							replyToId={parentCommentId}
							isOffline={isOffline}
						/>
					</RepliesFooter>
				) : (
					renderFooter()
				)}
			</div>
		</>
	);
};
